// SCRUM-1704 (SCRUM-1674 slice 2b): the one client read of the TaxonomyOverlay
// rows — `GET /api/taxonomy-overlays?site=<id>` (SCRUM-1703). Two readers: the
// page-create wizard's path derivation (PageFormContainer) and the per-site
// label screen (LabelCategories). No redux slice: each reader keeps the rows in
// local state for the site it is on, and the array is small (one row per
// (site, node) the site has labelled).
import type { AxiosResponse } from "axios";
import type { TaxonomyOverlayListResponse } from "../../../../shared/apiTypes";
import api from "../api";

export type TaxonomyOverlayRow = TaxonomyOverlayListResponse[number];

/**
 * The site's live overlay rows, `[]` when the site has none. Rejects the way
 * every other read does — the callers decide what "no rows" means to them.
 */
export const getTaxonomyOverlays = async (siteId: unknown): Promise<TaxonomyOverlayRow[]> => {
  const res: AxiosResponse<TaxonomyOverlayListResponse> = await api.get(`/api/taxonomy-overlays?site=${encodeURIComponent(String(siteId))}`);
  return Array.isArray(res?.data) ? res.data : [];
};

/**
 * The row for one node of one kind, or null. Ids are compared as strings: the
 * rows come back with `node` as an ObjectId string, the wizard hands over
 * whatever the picked entity carries.
 */
export function findOverlayRow(
  rows: TaxonomyOverlayRow[] | null | undefined,
  nodeKind: "category" | "subcategory",
  nodeId: unknown
): TaxonomyOverlayRow | null {
  if (!Array.isArray(rows) || nodeId === undefined || nodeId === null) return null;
  const wanted = String(nodeId);
  return (
    rows.find((row) => row && row.nodeKind === nodeKind && String(row.node) === wanted) ||
    null
  );
}

/**
 * The site-relative path an overlay row carries, or null when the row has no
 * usable slug: a category row may carry a label without a slug (SCRUM-1673's
 * only slug rule is uniqueness per site), and the empty slug SCRUM-1712 reserves
 * for the site-root listing is not a path to derive a page from either. Both
 * fall back to the `label[]` derivation the caller already has.
 */
export function overlayPathOf(row: TaxonomyOverlayRow | null | undefined): string | null {
  const slug = row && typeof row.slug === "string" ? row.slug.trim() : "";
  return slug === "" ? null : slug;
}
